import { Link, Button } from '@superlogica/button'
import { motion } from 'framer-motion'
import { rem, rgba } from 'polished'
import styled, { keyframes } from 'styled-components'

import media from '../../../utils/media'
import Icon from '../../helpers/Icon'
import Brand from '../Brand'

const barAnimation = keyframes`
  from {
    transform: translateY(-60px);
  }

  to {
    transform: rotate(0);
  }
`

export const HeaderNotification = styled.div`
  width: 100%;
  height: 45px;
  background-color: ${({ theme }) => theme.color.neutral700};
  color: ${({ theme }) => theme.color.neutral0};
  padding-top: 8px;
  padding-bottom: 8px;
  display: flex;
  align-items: center;

  ${media.lessThan('sm')`
    height: 60px;
  `}
`

export const HeaderNotificationWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`

export const HeaderNotificationText = styled.p`
  font-size: ${rem(14)};
  font-weight: 700;
  line-height: 1.2;
  margin: 0;

  ${media.lessThan('xs')`
    font-size: ${rem(12)};
  `}
`

export const HeaderNotificationLink = styled(Button)`
  text-transform: uppercase;
  flex-shrink: 0;

  span {
    color: ${({ theme }) => theme.color.neutral0} !important;
  }

  ${media.lessThan('xs')`
    margin-left: 0;

    span {
      display: none;
    }
  `}
`

export const HeaderChevron = styled(Icon)`
  transition: transform ease 0.4s;
  transform: rotate(0);
  margin-left: 12px;

  ${media.greaterThan('sm')`
    display: none;
  `}
`

export const HeaderWrapper = styled.header`
  width: 100%;
`

export const HeaderContent = styled.div`
  position: relative;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  height: 60px;
`

export const HeaderBrand = styled(Brand)`
  z-index: 2001;
`

export const HeaderMenu = styled.ul`
  display: flex;
  align-items: center;
  justify-content: space-between;

  ${media.lessThan('sm')`
    flex-direction: column;
    align-items: flex-start;
  `}
`

export const HeaderDropdown = styled(motion.div)`
  flex-direction: column;
  display: flex;
  z-index: 2001;
  overflow: hidden;

  ${media.greaterThan('sm')`
    position: absolute;
    visibility: hidden;
    top: 61px;
    width: 340px;
    height: auto !important;
    padding: 32px;
    border-bottom: 8px solid ${({ theme }) => theme.color.neutral200};
    background-color: ${({ theme }) => theme.color.white};
  `}

  ${media.lessThan('sm')`
    width: 100%;
  `}
`

export const HeaderDropdownLink = styled.a`
  display: flex;
  overflow: hidden;
  width: 100%;
  flex-shrink: 0;

  &:not(:last-of-type) {
    margin-bottom: 24px;
  }

  ${media.lessThan('sm')`
    overflow: hidden;

    &:first-of-type {
      margin-top: 24px;
    }
  `}
`

export const HeaderDropdownContent = styled.div`
  margin-left: 16px;
`

export const HeaderDropdownLabel = styled.span`
  display: block;
  text-transform: uppercase;
  font-weight: 700;
  font-size: ${rem(12)};
  color: ${({ theme, color }) => theme.color[color] || theme.color.neutral900};
`

export const HeaderDropdownHeadline = styled.span`
  display: block;
  font-size: ${rem(14)};
  line-height: 1.4;
`

export const HeaderNavItem = styled.li`
  position: relative;
  display: flex;

  ${({ isDropdown }) =>
    isDropdown &&
    `
    cursor: pointer;
  `}

  ${media.greaterThan('sm')`
    &:not(:first-of-type) {
      margin-left: ${({ theme }) => theme.margin.xs};
    }

    &:not(:last-of-type) {
      margin-right: ${({ theme }) => theme.margin.xs};
    }

    ${({ theme, isDropdown }) =>
      isDropdown &&
      `
      justify-content: center;

      ${HeaderNavLink} {
        pointer-events: none;
      }

      &.opened,
      &:hover {
        ${HeaderNavLink} {
          color: ${theme.color.neutral500};
        }

        ${HeaderDropdown} {
          opacity: 1 !important;
          visibility: visible !important;
        }
      }
    `}
  `}

  ${media.lessThan('sm')`
    width: calc(100% - 64px);
    margin-left: ${({ theme }) => theme.margin.sm};
    margin-right: ${({ theme }) => theme.margin.sm};

    ${({ isDropdown }) =>
      isDropdown &&
      `
      cursor: pointer;
      flex-direction: column;

      &.opened {
        ${HeaderNavLink} {
          margin-bottom: 24px;
        }

        ${HeaderChevron} {
          transform: rotate(180deg);
        }

        ${HeaderDropdown} {
          height: auto;
          visibility: visible;
          opacity: 1;
        }
      }
    `}
  `}
`

export const HeaderNavLink = styled.a`
  font-size: ${rem(14)};
  color: ${({ theme }) => theme.color.neutral900};

  &:hover {
    color: ${({ theme }) => theme.color.neutral500};
  }

  ${media.lessThan('sm')`
    width: 100%;
  `}
`

export const HeaderUtil = styled.div`
  display: flex;
  align-items: center;

  ${media.lessThan('sm')`
    width: calc(100% - 64px);
    margin-left: ${({ theme }) => theme.margin.sm};
    margin-right: ${({ theme }) => theme.margin.sm};
    padding: ${({ theme }) => theme.margin.sm} 0;
    border-top: 1px solid ${({ theme }) => theme.color.neutral300};
  `}
`

export const HeaderNav = styled.nav`
  ${media.greaterThan('sm')`
    ${HeaderMenu} {
      margin-left: auto;
      margin-right: auto;
    }
  `}
`

export const HeaderSubBarArrow = styled(Icon)`
  transition: transform ease 0.4s;
  transform: rotate(0);
`

export const HeaderSubBarToggle = styled.button`
  display: none;
  background-color: transparent;
  border: none;
  box-shadow: none;

  ${media.lessThan('sm')`
    display: block;
  `}
`

export const HeaderSubBarContent = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  height: 60px;

  ${media.greaterThan('sm')`
    border-bottom: 1px solid ${({ theme }) => theme.color.neutral300};
  `}
`

export const HeaderBar = styled.div`
  width: 100%;
  transition: all 0.4s ease-in-out;
  background-color: ${({ theme }) => theme.color.white};
  border-bottom: 1px solid ${({ theme }) => theme.color.neutral300};

  ${HeaderNav} {
    width: 100%;
    display: flex;
    justify-content: space-between;
  }

  ${media.greaterThan('sm')`
    ${HeaderNavLink} {
      &.active {
        &:after {
          content: '';
          display: block;
          background-color: ${({ theme }) => theme.color.neutral500};
          width: 100%;
          height: 1px;
          position: absolute;
          bottom: -1px;
        }
      }
    }

    ${HeaderNavItem} {
      display: flex;
      align-items: center;
      height: 60px;
    }
  `}

  ${media.lessThan('sm')`
    ${HeaderNav} {
      position: fixed;
      z-index: 2000;
      top: 120px;
      left: 0;
      flex-direction: column;
      width: 100%;
      height: calc(100% - 60px);
      background-color: ${({ theme }) => theme.color.white};
      transition: all 0.3s ease-in-out;
      transform: translateX(100%);
    }

    ${HeaderMenu} {
      padding-top: 14px;
      overflow-y: auto;
      -ms-overflow-style: none;
      height: calc(100% - 68px);
      justify-content: flex-start;

      &::-webkit-scrollbar {
        display: none;
      }
    }

    ${HeaderNavLink} {
      font-size: ${rem(16)};
    }

    ${HeaderNavItem} {
      margin-bottom: 24px;
      flex-shrink: 0;
    }

    &.menu-opened {
      ${HeaderNav} {
        transform: translateX(0%);
      }
    }
  `}
`

export const HeaderSubBar = styled.div`
  position: absolute;
  width: 100%;
  height: 60px;
  z-index: 1999;

  ${HeaderNavLink} {
    &.active {
      pointer-events: none;
      color: ${({ theme }) => theme.color.blue500};
    }
  }

  &.sticky-enabled {
    position: fixed;
    top: 0;
    background-color: ${({ theme }) => theme.color.neutral100};
    border-bottom: 1px solid ${({ theme }) => theme.color.neutral300};
    animation: ${barAnimation} 0.2s ease-in-out;
  }

  ${media.lessThan('sm')`
    border-bottom: 1px solid ${({ theme }) => theme.color.neutral300};

    ${HeaderNav} {
      position: absolute;
      z-index: 2000;
      top: calc(100% + 1px);
      left: 0;
      flex-direction: column;
      justify-content: center;
      width: 100%;
      opacity: 0;
      display: block;
      visibility: hidden;
      background-color: ${({ theme }) => theme.color.neutral100};
      transition: all 0.3s ease-in-out;
      overflow: hidden;
      box-shadow: 0 20px 19px -10px ${({ theme }) =>
        rgba(theme.color.neutral950, 0.15)};
    }

    ${HeaderMenu} {
      padding-top: 24px;
      padding-bottom: 24px;
    }

    ${HeaderNavItem} {
      &:not(:last-of-type) {
        margin-bottom: 16px;
        padding-bottom: 16px;
        border-bottom: 1px solid ${({ theme }) => theme.color.neutral300};
      }
    }

    &.submenu-expanded {
      ${HeaderNav} {
        opacity: 1;
        visibility: visible;
      }

      ${HeaderSubBarArrow} {
        transform: rotate(-180deg);
      }
    }
  `}
`

export const HeaderLogin = styled(Link)`
  flex-shrink: 0;
`

export const HeaderCta = styled(Button).attrs({
  color: 'blue500'
})`
  flex-shrink: 0;
  margin-left: 32px;

  ${media.lessThan('sm')`
    margin-left: 12px;
  `}
`
